import ScrollReveal from "@/components/base/ScrollReveal";

const rates = [
  {
    title: "Individual Therapy",
    range: "$65 – $200",
    per: "per session",
    description: "One-on-one therapy tailored to your goals, history, and pace. 45-50 minute sessions. Telehealth and in-person appointments available.",
    features: [
      "Telehealth or in-person",
      "Flexible scheduling",
      "Ongoing progress-focused care",
    ],
    icon: "ri-user-heart-line",
    iconColor: "#7D54FC",
    iconBg: "rgba(125,84,252,0.08)",
    iconBorder: "rgba(125,84,252,0.18)",
    accentColor: "#7D54FC",
  },
  {
    title: "Couples Therapy",
    range: "$200",
    per: "per session",
    description: "Joint sessions for partners working through conflict, communication, and connection. 45-50 minute sessions. Structured and evidence-based.",
    features: [
      "Telehealth or in-person",
      "Gottman-informed approach",
      "Flexible scheduling",
    ],
    icon: "ri-hearts-line",
    iconColor: "#3C6FF8",
    iconBg: "rgba(60,111,248,0.08)",
    iconBorder: "rgba(60,111,248,0.18)",
    accentColor: "#3C6FF8",
  },
];

export default function Pricing() {
  return (
    <section id="pricing" className="w-full scroll-mt-[108px] py-14 md:scroll-mt-[112px] md:py-20" style={{ background: "linear-gradient(160deg, #F8F5EE 0%, #F5F0E6 40%, #F2EDE0 100%)" }}>
      <div className="w-full px-6 md:px-12 lg:px-20">

        {/* Header */}
        <div className="max-w-xl mb-12 md:mb-14">
          <ScrollReveal variant="fadeIn" delay={0}>
            <span className="font-manrope text-[13px] font-semibold tracking-widest uppercase block mb-4" style={{ color: "#7D54FC" }}>
              Rates &amp; Payment Options
            </span>
          </ScrollReveal>
          <ScrollReveal variant="fadeUp" delay={80}>
            <h2 className="font-fraunces text-[#2B2B2B] text-4xl md:text-5xl font-light leading-snug mb-5">
              Clear pricing,<br />
              <em className="not-italic" style={{ color: "#7D54FC" }}>no surprises</em>
            </h2>
          </ScrollReveal>
          <ScrollReveal variant="fadeUp" delay={140}>
            <p className="font-manrope text-[#2B2B2B]/78 text-[15px] leading-relaxed max-w-lg">
              Session rates vary by therapist and are listed on each therapist&apos;s profile. The ranges below reflect our current pricing across the practice.
            </p>
          </ScrollReveal>
        </div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-10 xl:gap-14 items-start">

          {/* LEFT - Pricing Cards */}
          <div className="flex flex-col gap-5">
            {rates.map((r, i) => (
              <ScrollReveal key={r.title} variant="fadeUp" delay={i * 100} threshold={0.08}>
                <div
                  className="rounded-2xl p-7 border"
                  style={{ background: "rgba(255,255,255,0.80)", borderColor: r.iconBorder }}
                >
                  {/* Icon + title */}
                  <div className="flex items-center gap-4 mb-5">
                    <div
                      className="w-11 h-11 flex items-center justify-center rounded-full border shrink-0"
                      style={{ background: r.iconBg, borderColor: r.iconBorder }}
                    >
                      <i className={`${r.icon} text-lg`} style={{ color: r.iconColor }}></i>
                    </div>
                    <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium leading-snug">{r.title}</h3>
                  </div>

                  {/* Price range */}
                  <div className="mb-3">
                    <span className="font-fraunces text-3xl font-light" style={{ color: r.accentColor }}>{r.range}</span>
                    <span className="font-manrope text-xs text-[#2B2B2B]/40 ml-2">{r.per}</span>
                  </div>

                  <p className="font-manrope text-[15px] text-[#2B2B2B]/78 leading-relaxed mb-4">{r.description}</p>

                  <ul className="flex flex-col gap-2">
                    {r.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5">
                        <span className="w-4 h-4 flex items-center justify-center shrink-0">
                          <i className="ri-check-line text-sm" style={{ color: r.accentColor }}></i>
                        </span>
                        <span className="font-manrope text-[13px] text-[#2B2B2B]/80">{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </ScrollReveal>
            ))}
          </div>

          {/* RIGHT - Supporting Information */}
          <div className="flex flex-col gap-6">

            {/* Therapy Packages */}
            <ScrollReveal variant="fadeUp" delay={80}>
              <div
                className="rounded-2xl p-7 border"
                style={{ background: "rgba(255,255,255,0.80)", borderColor: "rgba(107,116,47,0.18)" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-full border shrink-0"
                    style={{ background: "rgba(107,116,47,0.09)", borderColor: "rgba(107,116,47,0.22)" }}
                  >
                    <i className="ri-stack-line text-base" style={{ color: "#6B742F" }}></i>
                  </div>
                  <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium">Therapy Packages</h3>
                </div>
                <p className="font-manrope text-[15px] text-[#2B2B2B]/80 leading-relaxed">
                  Therapy packages are available for clients who want consistency and a more focused approach to their work. Packages provide a clear structure and support ongoing progress over time.
                </p>
              </div>
            </ScrollReveal>

            {/* Courtesy Billing */}
            <ScrollReveal variant="fadeUp" delay={130}>
              <div
                className="rounded-2xl p-7 border"
                style={{ background: "rgba(255,255,255,0.80)", borderColor: "rgba(60,111,248,0.18)" }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-9 h-9 flex items-center justify-center rounded-full border shrink-0"
                    style={{ background: "rgba(60,111,248,0.08)", borderColor: "rgba(60,111,248,0.20)" }}
                  >
                    <i className="ri-shield-check-line text-base" style={{ color: "#3C6FF8" }}></i>
                  </div>
                  <h3 className="font-fraunces text-[#2B2B2B] text-xl font-medium">Courtesy Billing</h3>
                </div>
                <p className="font-manrope text-[15px] text-[#2B2B2B]/80 leading-relaxed">
                  We offer courtesy billing. We bill you directly and submit claims to your insurance for potential reimbursement, so you don&apos;t have to manage the process yourself.
                </p>
              </div>
            </ScrollReveal>

            {/* CTA */}
            <ScrollReveal variant="fadeUp" delay={180}>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center pt-1">
                <a
                  href="/full-pricing"
                  className="inline-flex items-center gap-2 font-manrope font-semibold text-[15px] px-7 py-4 rounded-full cursor-pointer whitespace-nowrap transition-colors text-white"
                  style={{ background: "#3C6FF8" }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = "#2a5de6"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.background = "#3C6FF8"; }}
                >
                  View Full Pricing <i className="ri-arrow-right-line"></i>
                </a>
                <a
                  href="/contact"
                  className="inline-flex items-center gap-1.5 font-manrope text-[15px] font-medium text-[#2B2B2B]/72 hover:text-[#2B2B2B] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Have questions? Contact us <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </ScrollReveal>

          </div>
        </div>

      </div>
    </section>
  );
}
